import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ResourceBar } from '@Components/Flows';
import {
  GuestDetailsContainer,
  GuestProfileImage,
  GuestQuickDetails,
  type GuestDetailsTab,
  type GuestSpendSummary,
} from '@Components/Guest';
import { useFish } from '@Hooks/useFish';
import { useActivity } from '@Hooks/useActivity';
import { cn } from '@Utils/cn';
import {
  FishDetailsProvider,
  useFishDetails,
} from '@Views/fish-details/FishDetailsContext';
import FishDetailsOverlays from '@Views/fish-details/FishDetailsOverlays';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export type InboxFishPaneTheme = 'light' | 'dark';

/**
 * Surface classes per theme. The Guest components and fish-details panels
 * render light unless an ancestor carries `.dark`, so the dark theme sets it.
 */
const THEME_CLASSES: Record<
  InboxFishPaneTheme,
  { bar: string; details: string; panel: string }
> = {
  dark: {
    bar: 'dark flex w-full shrink-0 flex-col items-start bg-zinc-800',
    details: 'dark flex h-full w-full flex-col gap-2.5 bg-zinc-700 px-8 pt-4 pb-6',
    panel: 'scrollbar-dark min-h-0 flex-1 overflow-y-auto pt-2 text-zinc-100',
  },
  light: {
    bar: 'flex w-full shrink-0 flex-col items-start border-b border-aoh-border bg-white',
    details: 'flex h-full w-full flex-col gap-2.5 bg-aoh-surface px-8 pt-4 pb-6',
    panel: 'min-h-0 flex-1 overflow-y-auto pt-2 text-aoh-ink',
  },
};

/** The fish details page's tabs, minus Messages: the open thread is the conversation. */
const BASE_TABS: GuestDetailsTab[] = ['overview', 'activity', 'tags', 'profile'];

interface InboxFishDetailsProps {
  activeTab: GuestDetailsTab;
  onTabChange: (tab: GuestDetailsTab) => void;
  spendSummary: GuestSpendSummary;
  theme: InboxFishPaneTheme;
}

function InboxFishDetails({
  activeTab,
  onTabChange,
  spendSummary,
  theme,
}: InboxFishDetailsProps) {
  const { details, panel } = THEME_CLASSES[theme];
  const { actor } = useFishDetails();
  const tabs: GuestDetailsTab[] = actor?.subscriptions?.length
    ? [...BASE_TABS, 'membership']
    : BASE_TABS;

  return (
    <GuestDetailsContainer
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      spendSummary={spendSummary}
      className={details}
      panelClassName={panel}
    />
  );
}

interface InboxFishPaneProps {
  fishId: string;
  /** Defaults to the dark surface the flow page's guest bar uses. */
  theme?: InboxFishPaneTheme;
  /** The thread's scrolling message list; the details pane slides down over it. */
  children: React.ReactNode;
}

/**
 * The flow page's guest bar for the fish on the open inbox thread. The bar
 * reads the react-query fish and activity caches; FishDetailsProvider mounts
 * on the first expand for a fish and stays mounted until the fish changes,
 * keyed by fishId because it does not reset its state when the id changes.
 */
export function InboxFishPane({
  fishId,
  theme = 'dark',
  children,
}: InboxFishPaneProps) {
  const [expandedFishId, setExpandedFishId] = useState<string | null>(null);
  const [loadedFishId, setLoadedFishId] = useState<string | null>(null);
  const [guestTab, setGuestTab] = useState<GuestDetailsTab>('overview');

  const { data: fish } = useFish({ fishId });
  const { activity } = useActivity({ fishId });

  const isExpanded = expandedFishId === fishId;
  const isLoaded = loadedFishId === fishId;

  const fullName = `${fish?.preferredProfile?.fName ?? '-'} ${fish?.preferredProfile?.lName ?? '-'}`;
  const isMember = !!fish?.subscriptions?.some(
    (sub) => sub.cancelledAt === null && sub.deletedAt === null,
  );
  const spendSummary = {
    mtdSpend: usd.format(Number(activity?.meta?.mtdTotal ?? 0)),
    ytdSpend: usd.format(Number(activity?.meta?.ytdTotal ?? 0)),
    allTimeSpend: usd.format(Number(activity?.meta?.allTotal ?? 0)),
  };

  const toggleDetails = () => {
    if (isExpanded) {
      setExpandedFishId(null);
      return;
    }
    setExpandedFishId(fishId);
    setLoadedFishId(fishId);
  };

  return (
    <>
      <ResourceBar
        isDetailsExpanded={isExpanded}
        onToggleDetails={toggleDetails}
        className={THEME_CLASSES[theme].bar}
      >
        <GuestProfileImage
          cloudinaryPublicId={fish?.mostRecentAsset?.cloudinaryId}
          imageUrl={null}
          alt={fullName}
          className="size-20"
        />
        <GuestQuickDetails
          fullName={fullName}
          totalSpend={spendSummary.allTimeSpend}
          isClubMember={isMember}
        />
      </ResourceBar>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {children}
        <div
          className={cn(
            'absolute inset-0 transition-[translate,visibility] duration-300 ease-in-out',
            isExpanded ? 'visible translate-y-0' : 'invisible -translate-y-full',
          )}
        >
          {isLoaded && (
            <FishDetailsProvider key={fishId} fishId={fishId} variant="guest">
              <InboxFishDetails
                activeTab={guestTab}
                onTabChange={setGuestTab}
                spendSummary={spendSummary}
                theme={theme}
              />
              {/* The legacy Modal is fixed and inline; portalled to body so the
                  sliding pane's transform doesn't become its containing block
                  and it keeps a light surface. */}
              {createPortal(<FishDetailsOverlays />, document.body)}
            </FishDetailsProvider>
          )}
        </div>
      </div>
    </>
  );
}
